import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ToolPage, { inputCls, labelCls, primaryBtn, secondaryBtn } from "@/components/ToolPage";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SectionLabel } from "@/components/Eyebrow";
import { Upload, FileText, ArrowRight, Check, AlertTriangle } from "lucide-react";

const STATUS_COLOR = {
  on_track: "text-ivy", completed: "text-ivy", ready: "text-ivy",
  at_risk: "text-brass", almost_ready: "text-brass",
  behind: "text-ink", needs_work: "text-ink",
};

export default function TranscriptAudit() {
  const { user } = useAuth();
  const [stage, setStage] = useState("form");
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [intendedMajor, setIntendedMajor] = useState("Business Administration");
  const [targetSchool, setTargetSchool] = useState("UF");
  const [email, setEmail] = useState(""); const [name, setName] = useState("");
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const onDrop = (e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) setFile(f); };

  const run = async (e) => {
    e?.preventDefault?.();
    if (!file) { toast.error("Please choose a PDF transcript."); return; }
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Only PDF files are supported."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10MB."); return; }
    if (!user && (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) { toast.error("Please enter a valid email."); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("intended_major", intendedMajor);
      fd.append("target_school", targetSchool);
      if (email) fd.append("email", email);
      if (name) fd.append("name", name);
      const res = await api.post("/tools/transcript-audit", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setResult(res.data); setStage("result");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transcript audit failed.");
    } finally { setLoading(false); }
  };

  return (
    <ToolPage
      numeral="VI"
      name="Transcript Audit"
      blurb="Upload your unofficial transcript. We extract the courses, map them against the CPM for your major, and return a full structured audit."
      seoTitle="Free AI Transcript Audit for Florida Transfer Students — UF, UCF, FSU | Beyond Valencia"
      seoDescription="Upload your Florida community college transcript. Our AI maps completed courses against the Common Prerequisites Manual for your intended major and target university."
      seoCanonical="/tools/transcript-audit"
      seoKeywords="transcript audit, transcript review, UF prereqs, Florida transfer transcript, unofficial transcript audit"
    >
      {stage === "form" && (
        <form onSubmit={run} className="max-w-3xl space-y-7" data-testid="transcript-form">
          <SectionLabel index="A" label="Upload" />
          <div
            onDragOver={(e) => e.preventDefault()} onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className="border-2 border-dashed border-stone/50 bg-bone p-12 text-center cursor-pointer hover:border-ivy transition-colors"
            data-testid="transcript-dropzone"
          >
            <Upload className="h-8 w-8 text-brass mx-auto" />
            <p className="mt-4 font-serif text-xl text-ink tracking-tight">{file ? file.name : "Drag your PDF here, or click to choose"}</p>
            <p className="mt-2 text-xs text-stone uppercase tracking-[0.18em]">Text-selectable PDFs only · max 10MB</p>
            <input ref={inputRef} type="file" accept="application/pdf" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="transcript-file-input" />
          </div>

          <SectionLabel index="B" label="Context" />
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2"><Label className={labelCls}>Intended major</Label><Input data-testid="transcript-major" className={inputCls} value={intendedMajor} onChange={(e) => setIntendedMajor(e.target.value)} required /></div>
            <div className="space-y-2"><Label className={labelCls}>Target school</Label><Input data-testid="transcript-target" className={inputCls} value={targetSchool} onChange={(e) => setTargetSchool(e.target.value)} required /></div>
          </div>

          {!user && (
            <>
              <SectionLabel index="C" label="Where to send the audit" />
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2"><Label className={labelCls}>First name</Label><Input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} /></div>
                <div className="space-y-2"><Label className={labelCls}>Email</Label><Input type="email" data-testid="transcript-email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
              </div>
              <p className="text-[11px] text-stone uppercase tracking-[0.18em]">Free audits are limited to one per email. After that, a paid Transfer Audit ($249) is required.</p>
            </>
          )}
          <button type="submit" disabled={loading} className={primaryBtn} data-testid="transcript-submit-btn">{loading ? "Auditing transcript… this can take 30-60 seconds" : "Run the audit"}</button>
        </form>
      )}

      {stage === "result" && result && (
        <div className="space-y-10" data-testid="transcript-result">
          <div className="border border-stone/40 bg-bone-dim p-7 flex items-baseline justify-between flex-wrap gap-4">
            <div><div className="text-[10px] uppercase tracking-[0.22em] text-brass">Applicant type</div><p className="font-serif text-2xl text-ink mt-2 tracking-tight">{result.applicant_type}</p></div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-stone">Audit ID · {result.audit_id}</div>
          </div>

          <div>
            <SectionLabel index="01" label="Parsed courses" />
            <div className="mt-5 border-t border-l border-stone/40">
              <div className="border-r border-b border-stone/40 grid grid-cols-12 bg-bone-dim p-3 text-[10px] uppercase tracking-[0.18em] text-stone"><div className="col-span-3">Code</div><div className="col-span-5">Name</div><div className="col-span-2">Term</div><div className="col-span-1 text-right">Cr</div><div className="col-span-1 text-right">Grade</div></div>
              {(result.parsed_courses || []).map((c, i) => (
                <div key={i} className="border-r border-b border-stone/40 grid grid-cols-12 bg-bone p-3 text-sm">
                  <div className="col-span-3 font-mono text-xs uppercase tracking-[0.18em] text-stone tabular-nums">{c.code}</div>
                  <div className="col-span-5 text-ink">{c.name}</div>
                  <div className="col-span-2 text-stone text-xs">{c.term || "—"}</div>
                  <div className="col-span-1 text-right text-ink tabular-nums">{c.credits}</div>
                  <div className="col-span-1 text-right text-brass font-semibold">{c.grade}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-0 border-t border-l border-stone/40">
            <div className="border-r border-b border-stone/40 bg-bone p-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-ivy">Completed prereqs</div>
              <ul className="mt-4 space-y-3">{(result.completed_prereqs || []).map((p, i) => (<li key={i} className="flex gap-2.5 text-sm"><Check className="h-4 w-4 text-ivy flex-shrink-0 mt-0.5" /><div><div className="text-ink font-medium">{p.course}</div><div className="text-stone text-xs">Satisfies: {p.satisfies}</div>{p.notes && <div className="text-stone text-xs italic mt-1">{p.notes}</div>}</div></li>))}</ul>
            </div>
            <div className="border-r border-b border-stone/40 bg-bone p-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Missing prereqs</div>
              <ul className="mt-4 space-y-3">{(result.missing_prereqs || []).map((p, i) => (<li key={i} className="flex gap-2.5 text-sm"><AlertTriangle className="h-4 w-4 text-brass flex-shrink-0 mt-0.5" /><div><div className="text-ink font-medium">{p.course}</div><div className="text-stone text-xs">{p.reason_required}</div><div className="text-stone text-xs">Suggested: {p.suggested_semester}</div></div></li>))}</ul>
            </div>
          </div>

          <div className="border border-stone/40 bg-bone p-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass">AA progress</div>
            <div className="mt-4 flex items-baseline justify-between"><span className="font-serif text-3xl text-ink tabular-nums">{result.aa_completion?.credits_completed} / 60</span><span className={`text-sm uppercase tracking-[0.18em] font-semibold ${STATUS_COLOR[result.aa_completion?.status] || "text-stone"}`}>{result.aa_completion?.status?.replace(/_/g, " ")}</span></div>
            <div className="mt-3 h-[3px] bg-stone/30 overflow-hidden"><div className="h-full bg-brass" style={{ width: `${Math.min(100, ((result.aa_completion?.credits_completed || 0) / 60) * 100)}%` }} /></div>
            <p className="mt-3 text-xs text-stone">~{result.aa_completion?.credits_remaining_estimate} credits remaining (estimate).</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-0 border-t border-l border-stone/40">
            <div className="border-r border-b border-stone/40 bg-bone p-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Excess credit risk</div>
              <p className={`mt-3 font-serif text-xl tracking-tight ${result.excess_credit_risk?.flagged ? "text-brass" : "text-ivy"}`}>{result.excess_credit_risk?.flagged ? `Flagged (~${result.excess_credit_risk.count_estimate})` : "Not flagged"}</p>
              <p className="mt-3 text-sm text-ink-soft leading-relaxed">{result.excess_credit_risk?.notes}</p>
            </div>
            <div className="border-r border-b border-stone/40 bg-bone p-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brass">GPA</div>
              <div className="mt-3 grid grid-cols-2 gap-3"><div><div className="text-[10px] uppercase tracking-[0.18em] text-stone">Overall</div><div className="font-serif text-2xl text-ink tabular-nums">{result.gpa_summary?.overall ?? "—"}</div></div><div><div className="text-[10px] uppercase tracking-[0.18em] text-stone">Major/sci est.</div><div className="font-serif text-2xl text-ink tabular-nums">{result.gpa_summary?.science_or_major_estimate ?? "—"}</div></div></div>
              <p className="mt-3 text-xs text-stone">{result.gpa_summary?.notes}</p>
            </div>
          </div>

          <div className="border border-stone/40 bg-bone p-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Risk analysis</div>
            <ul className="mt-4 space-y-2">{(result.risk_analysis || []).map((r, i) => (<li key={i} className="flex gap-2 text-sm text-ink"><span className="text-brass mt-2 h-px w-3 bg-brass flex-shrink-0" /><span>{r}</span></li>))}</ul>
          </div>

          <div className="border border-stone/40 bg-bone p-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Next semester recommendations</div>
            <ol className="mt-4 space-y-3">{(result.next_semester_recommendations || []).map((r, i) => (<li key={i} className="border-l-2 border-brass pl-4"><div className="font-serif text-lg text-ink tracking-tight">{r.course_pattern}</div><div className="text-sm text-ink-soft mt-1">{r.why}</div></li>))}</ol>
          </div>

          <div className="border border-ivy bg-ivy text-bone p-7">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass-soft">Target university readiness · {result.target_university_readiness?.school}</div>
            <p className="mt-3 font-serif font-medium text-3xl leading-snug tracking-tight">{result.target_university_readiness?.status?.replace(/_/g, " ").toUpperCase()}</p>
            <p className="mt-4 text-bone-dim leading-relaxed">{result.target_university_readiness?.explanation}</p>
          </div>

          <div className="text-center border-t border-stone/30 pt-10">
            <p className="font-serif text-2xl text-ink tracking-tight">Want a human review of this audit?</p>
            <Link to="/transfer-audit" className="inline-flex items-center gap-2 mt-6 bg-brass hover:bg-brass-soft text-ink uppercase tracking-[0.18em] text-xs font-semibold py-4 px-8 transition-colors" data-testid="transcript-result-audit-cta">Schedule a Transfer Audit ($249) <ArrowRight className="h-3 w-3" /></Link>
          </div>
          <p className="text-[11px] text-stone italic">{result.disclaimer}</p>
        </div>
      )}
    </ToolPage>
  );
}
